import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@repo/api-client";
import type {
  AdminDashboardResponse,
  AdminUserRecord,
  GuidedPractice,
  GuidedPracticeInput,
  SiteNavLink,
  SiteSettingsRecord,
} from "@repo/shared-types";
import { useTheme, type AppTheme } from "../lib/theme-context";

/* ─── Theme preview cards ─────────────────────────────────────────── */
interface ThemeCardProps {
  id: AppTheme;
  name: string;
  description: string;
  palette: string[];
  fontLabel: string;
  layoutLabel: string;
  active: boolean;
  onSelect: (id: AppTheme) => void;
}

function ThemeCard({
  id,
  name,
  description,
  palette,
  fontLabel,
  layoutLabel,
  active,
  onSelect,
}: ThemeCardProps) {
  return (
    <button
      type="button"
      className={`admin-theme-card${active ? " admin-theme-card--active" : ""}`}
      onClick={() => onSelect(id)}
      aria-pressed={active}
    >
      {active && <span className="admin-theme-card__badge">Active</span>}
      {/* Mini palette swatches */}
      <div className="admin-theme-card__palette">
        {palette.map((c) => (
          <span
            key={c}
            className="admin-theme-card__swatch"
            style={{ background: c }}
            aria-hidden="true"
          />
        ))}
      </div>
      <h3 className="admin-theme-card__name">{name}</h3>
      <p className="admin-theme-card__desc">{description}</p>
      <div className="admin-theme-card__meta">
        <span>🔤 {fontLabel}</span>
        <span>📐 {layoutLabel}</span>
      </div>
    </button>
  );
}

/* ─── Toggle setting row ──────────────────────────────────────────── */
function SettingToggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="admin-toggle-row">
      <div className="admin-toggle-row__text">
        <span className="admin-toggle-row__label">{label}</span>
        <span className="admin-toggle-row__hint">{hint}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        className={`admin-toggle${value ? " admin-toggle--on" : ""}`}
        onClick={() => onChange(!value)}
      >
        <span className="admin-toggle__thumb" />
      </button>
    </label>
  );
}

/* ─── AdminPage ───────────────────────────────────────────────────── */
export function AdminPage() {
  const { settings, updateSettings, setTheme } = useTheme();
  const [dashboard, setDashboard] = useState<AdminDashboardResponse | null>(null);
  const [headerLinks, setHeaderLinks] = useState<SiteNavLink[]>(settings.headerLinks);
  const [footerLinks, setFooterLinks] = useState<SiteNavLink[]>(settings.footerLinks);
  const [guidedPractices, setGuidedPractices] = useState<GuidedPractice[]>([]);
  const [editingGuidedId, setEditingGuidedId] = useState<string | null>(null);
  const [guidedForm, setGuidedForm] = useState<GuidedPracticeInput>({
    title: "",
    description: "",
    practice_type: "meditation",
    duration_minutes: 10,
    script: "",
    steps: [],
    is_published: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, persistedSettings, guided] = await Promise.all([
        api.getAdminDashboard(),
        api.getAdminSettings(),
        api.listAdminGuidedPractices(),
      ]);
      setDashboard(data);
      updateSettings(persistedSettings);
      setHeaderLinks(persistedSettings.headerLinks);
      setFooterLinks(persistedSettings.footerLinks);
      setGuidedPractices(guided);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  }, [updateSettings]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  async function handleSave() {
    const payload: SiteSettingsRecord = {
      ...settings,
      headerLinks,
      footerLinks,
    };
    await api.updateAdminSettings(payload);
    updateSettings(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function updateLink(
    area: "header" | "footer",
    linkId: string,
    patch: Partial<SiteNavLink>
  ) {
    const updater = (links: SiteNavLink[]) =>
      links.map((link) => (link.id === linkId ? { ...link, ...patch } : link));

    if (area === "header") {
      setHeaderLinks((prev) => updater(prev));
      return;
    }

    setFooterLinks((prev) => updater(prev));
  }

  function addLink(area: "header" | "footer") {
    const next: SiteNavLink = {
      id: crypto.randomUUID(),
      label: "New Link",
      href: area === "header" ? "/" : "/support",
      kind: "internal",
      area,
      enabled: true,
      order: area === "header" ? headerLinks.length + 1 : footerLinks.length + 1,
    };

    if (area === "header") {
      setHeaderLinks((prev) => [...prev, next]);
      return;
    }

    setFooterLinks((prev) => [...prev, next]);
  }

  async function saveGuidedPractice() {
    const payload: GuidedPracticeInput = {
      ...guidedForm,
      steps: guidedForm.steps.filter((step) => step.trim().length > 0),
    };

    if (editingGuidedId) {
      await api.updateAdminGuidedPractice(editingGuidedId, payload);
    } else {
      await api.createAdminGuidedPractice(payload);
    }

    const next = await api.listAdminGuidedPractices();
    setGuidedPractices(next);
    setEditingGuidedId(null);
    setGuidedForm({
      title: "",
      description: "",
      practice_type: "meditation",
      duration_minutes: 10,
      script: "",
      steps: [],
      is_published: true,
    });
    setNotice("Guided practice saved.");
  }

  async function deleteGuidedPractice(practiceId: string) {
    await api.deleteAdminGuidedPractice(practiceId);
    const next = await api.listAdminGuidedPractices();
    setGuidedPractices(next);
    setNotice("Guided practice deleted.");
  }

  function beginEditGuidedPractice(item: GuidedPractice) {
    setEditingGuidedId(item.id);
    setGuidedForm({
      title: item.title,
      description: item.description,
      practice_type: item.practice_type,
      duration_minutes: item.duration_minutes,
      script: item.script,
      steps: item.steps,
      is_published: item.is_published,
    });
  }

  const filteredUsers = useMemo<AdminUserRecord[]>(() => {
    const q = search.trim().toLowerCase();
    const users = dashboard?.users ?? [];
    if (!q) return users;
    return users.filter(
      (u) =>
        u.display_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [dashboard?.users, search]);

  async function setUserRole(userId: string, role: "user" | "admin") {
    setUpdatingUserId(userId);
    setNotice(null);
    try {
      await api.updateAdminUserRole(userId, { role });
      await loadDashboard();
      setNotice("Role updated successfully.");
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to update user role.");
    } finally {
      setUpdatingUserId(null);
    }
  }

  const summary = dashboard?.summary;
  const signupsSeries = dashboard?.signups_series ?? [];
  const practiceSeries = dashboard?.practice_series ?? [];
  const recentActivity = dashboard?.recent_activity ?? [];
  const health = dashboard?.health;

  const themes: Omit<ThemeCardProps, "active" | "onSelect">[] = [
    {
      id: "modern",
      name: "Aekam Modern",
      description:
        "Unified warm spiritual system with sacred-gold accents, immersive hero gradients, and dashboard-friendly cards.",
      palette: ["#D4A853", "#C08B3F", "#FDF8F0", "#3D2B1F", "#1A1207"],
      fontLabel: "Playfair Display + Inter",
      layoutLabel: "Full-width, top-nav",
    },
    {
      id: "zen",
      name: "Zen Legacy",
      description:
        "Classic Aekam experience: earthy linen tones, Playfair Display / Inter typography, sidebar navigation, and serene bottom-nav mobile layout.",
      palette: ["#faf9f7", "#ede8dc", "#ff6b35", "#443729", "#f5f2ed"],
      fontLabel: "Playfair Display + Inter",
      layoutLabel: "Compact header, bottom-nav",
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div className="container">
          <div className="admin-page__breadcrumb">⚙️ Site Administration</div>
          <h1 className="admin-page__title">Admin Panel</h1>
          <p className="admin-page__subtitle">
            Operations, content controls, and site configuration in one place.
          </p>
        </div>
      </div>

      <div className="container admin-page__body">
        {loading && (
          <section className="admin-section">
            <p className="admin-section__desc">Loading live admin data…</p>
          </section>
        )}

        {!loading && error && (
          <section className="admin-section">
            <h2 className="admin-section__title">
              <span aria-hidden="true">⚠️</span> Admin data unavailable
            </h2>
            <p className="admin-section__desc">{error}</p>
          </section>
        )}

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">📈</span> Admin Dashboard
          </h2>
          <div className="admin-kpi-grid">
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">Total Users</span>
              <strong className="admin-kpi-card__value">{summary?.total_users ?? 0}</strong>
            </article>
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">Active Today</span>
              <strong className="admin-kpi-card__value">{summary?.active_today ?? 0}</strong>
            </article>
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">New Signups (7d)</span>
              <strong className="admin-kpi-card__value">{summary?.new_signups_7d ?? 0}</strong>
            </article>
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">Practice Minutes (7d)</span>
              <strong className="admin-kpi-card__value">{summary?.practice_minutes_7d ?? 0}</strong>
            </article>
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">Admin Users</span>
              <strong className="admin-kpi-card__value">{summary?.admin_users ?? 0}</strong>
            </article>
            <article className="admin-kpi-card">
              <span className="admin-kpi-card__label">QR Checks (7d)</span>
              <strong className="admin-kpi-card__value">{summary?.qr_checks_7d ?? 0}</strong>
            </article>
          </div>

          <div className="admin-chart-grid">
            <article className="admin-chart-card">
              <h3>Signup Chart (12 weeks)</h3>
              <div className="admin-bars" aria-label="Signup chart">
                {signupsSeries.map((value, i) => (
                  <span
                    key={`signup-${i}`}
                    className="admin-bars__bar"
                    style={{ height: `${Math.max(20, value * 2)}px` }}
                    title={`${value} signups`}
                  />
                ))}
              </div>
            </article>

            <article className="admin-chart-card">
              <h3>Practice Minutes (12 weeks)</h3>
              <div className="admin-line" aria-label="Revenue chart">
                {practiceSeries.map((value, i) => (
                  <span
                    key={`practice-${i}`}
                    className="admin-line__dot"
                    style={{ bottom: `${Math.max(8, Math.round(value / 22))}px` }}
                    title={`${value} minutes`}
                  />
                ))}
              </div>
            </article>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🧩</span> Management Links
          </h2>
          <div className="admin-link-row">
            <a className="btn btn--brand" href="/wp-admin" target="_blank" rel="noreferrer">
              Open WordPress Admin
            </a>
            <a className="btn btn--outline" href="/prestashop/admin" target="_blank" rel="noreferrer">
              Open PrestaShop Back Office
            </a>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">👥</span> User Management
          </h2>
          <div className="admin-users-toolbar">
            <input
              type="search"
              className="admin-field__input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search users"
            />
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Tier</th>
                  <th>Join Date</th>
                  <th>Last Active</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.display_name}</td>
                    <td>{u.email}</td>
                    <td style={{ textTransform: "capitalize" }}>{u.tier}</td>
                    <td>{u.created_at.slice(0, 10)}</td>
                    <td>{u.last_active ? u.last_active.slice(0, 10) : "—"}</td>
                    <td>
                      <select
                        className="admin-table__select"
                        value={u.role}
                        onChange={(e) => void setUserRole(u.id, e.target.value as "user" | "admin")}
                        aria-label={`Role for ${u.display_name}`}
                        disabled={updatingUserId === u.id}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🪝</span> Recent Activity
          </h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((row) => (
                  <tr key={row.id}>
                    <td>{row.timestamp.replace("T", " ").slice(0, 19)}</td>
                    <td style={{ textTransform: "capitalize" }}>{row.kind}</td>
                    <td>{row.title}</td>
                    <td>
                      <span className={`admin-status admin-status--${row.status}`}>{row.status}</span>
                    </td>
                    <td>{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🩺</span> System Health
          </h2>
          <div className="admin-health-grid">
            <article className="admin-health-card">
              <span className="admin-health-card__name">API Health</span>
              <strong className="admin-health-card__metric">{health?.api ?? "healthy"}</strong>
              <span className="admin-health-card__meta">
                <span className={`admin-dot admin-dot--${(health?.api ?? "healthy") === "healthy" ? "ok" : "warning"}`} aria-hidden="true" />
                Live admin endpoint
              </span>
            </article>
            <article className="admin-health-card">
              <span className="admin-health-card__name">Database</span>
              <strong className="admin-health-card__metric">{health?.database ?? "healthy"}</strong>
              <span className="admin-health-card__meta">
                <span className={`admin-dot admin-dot--${(health?.database ?? "healthy") === "healthy" ? "ok" : "warning"}`} aria-hidden="true" />
                Supabase profiles + activity tables
              </span>
            </article>
            <article className="admin-health-card">
              <span className="admin-health-card__name">Auth</span>
              <strong className="admin-health-card__metric">{health?.auth ?? "healthy"}</strong>
              <span className="admin-health-card__meta">
                <span className={`admin-dot admin-dot--${(health?.auth ?? "healthy") === "healthy" ? "ok" : "warning"}`} aria-hidden="true" />
                Role claims sourced from persisted profiles
              </span>
            </article>
          </div>
          <p className="admin-section__desc">{health?.note}</p>
        </section>

        {/* ── Theme Selector ──────────────────────────────────── */}
        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🎨</span> Theme
          </h2>
          <p className="admin-section__desc">
            Choose the visual identity for your spiritual platform. The Zen Legacy theme
            replicates the original Aekam site appearance exactly.
          </p>
          <div className="admin-theme-grid">
            {themes.map((t) => (
              <ThemeCard
                key={t.id}
                {...t}
                active={settings.theme === t.id}
                onSelect={setTheme}
              />
            ))}
          </div>
        </section>

        {/* ── Site Identity ───────────────────────────────────── */}
        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🏷️</span> Site Identity
          </h2>
          <div className="admin-field-grid">
            <label className="admin-field">
              <span className="admin-field__label">Site Name</span>
              <input
                type="text"
                className="admin-field__input"
                value={settings.siteName}
                onChange={(e) => updateSettings({ siteName: e.target.value })}
                maxLength={60}
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Tagline</span>
              <input
                type="text"
                className="admin-field__input"
                value={settings.tagline}
                onChange={(e) => updateSettings({ tagline: e.target.value })}
                maxLength={80}
              />
            </label>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🔗</span> Header &amp; Footer Links
          </h2>
          <p className="admin-section__desc">
            Manage navigation links shown in the site header and footer without changing code.
          </p>

          <div className="admin-field-grid">
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <strong>Header Links</strong>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => addLink("header")}>+ Add</button>
              </div>
              {headerLinks
                .slice()
                .sort((left, right) => left.order - right.order)
                .map((link) => (
                  <div key={link.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.4rem", marginBottom: "0.4rem" }}>
                    <input
                      className="admin-field__input"
                      value={link.label}
                      onChange={(e) => updateLink("header", link.id, { label: e.target.value })}
                      placeholder="Label"
                    />
                    <input
                      className="admin-field__input"
                      value={link.href}
                      onChange={(e) => updateLink("header", link.id, { href: e.target.value })}
                      placeholder="/path or https://"
                    />
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => updateLink("header", link.id, { enabled: !link.enabled })}
                    >
                      {link.enabled ? "On" : "Off"}
                    </button>
                  </div>
                ))}
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <strong>Footer Links</strong>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => addLink("footer")}>+ Add</button>
              </div>
              {footerLinks
                .slice()
                .sort((left, right) => left.order - right.order)
                .map((link) => (
                  <div key={link.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.4rem", marginBottom: "0.4rem" }}>
                    <input
                      className="admin-field__input"
                      value={link.label}
                      onChange={(e) => updateLink("footer", link.id, { label: e.target.value })}
                      placeholder="Label"
                    />
                    <input
                      className="admin-field__input"
                      value={link.href}
                      onChange={(e) => updateLink("footer", link.id, { href: e.target.value })}
                      placeholder="/path or https://"
                    />
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => updateLink("footer", link.id, { enabled: !link.enabled })}
                    >
                      {link.enabled ? "On" : "Off"}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </section>

        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🧘</span> Guided Practice Authoring
          </h2>
          <p className="admin-section__desc">
            Create and manage guided practices with timed duration and script instructions.
          </p>

          <div className="admin-field-grid">
            <label className="admin-field">
              <span className="admin-field__label">Title</span>
              <input
                className="admin-field__input"
                value={guidedForm.title}
                onChange={(e) => setGuidedForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Type</span>
              <select
                className="admin-field__input"
                value={guidedForm.practice_type}
                onChange={(e) => setGuidedForm((prev) => ({ ...prev, practice_type: e.target.value as GuidedPracticeInput["practice_type"] }))}
              >
                <option value="meditation">Meditation</option>
                <option value="yoga">Yoga</option>
                <option value="breathing">Breathing</option>
                <option value="reading">Reading</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Duration (minutes)</span>
              <input
                className="admin-field__input"
                type="number"
                min={1}
                value={guidedForm.duration_minutes}
                onChange={(e) => setGuidedForm((prev) => ({ ...prev, duration_minutes: Number(e.target.value) || 1 }))}
              />
            </label>
          </div>

          <label className="admin-field" style={{ marginTop: "0.75rem" }}>
            <span className="admin-field__label">Description</span>
            <textarea
              className="admin-field__input"
              rows={2}
              value={guidedForm.description}
              onChange={(e) => setGuidedForm((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>

          <label className="admin-field" style={{ marginTop: "0.75rem" }}>
            <span className="admin-field__label">Guided Script</span>
            <textarea
              className="admin-field__input"
              rows={4}
              value={guidedForm.script}
              onChange={(e) => setGuidedForm((prev) => ({ ...prev, script: e.target.value }))}
            />
          </label>

          <label className="admin-field" style={{ marginTop: "0.75rem" }}>
            <span className="admin-field__label">Steps (one per line)</span>
            <textarea
              className="admin-field__input"
              rows={4}
              value={guidedForm.steps.join("\n")}
              onChange={(e) => setGuidedForm((prev) => ({ ...prev, steps: e.target.value.split("\n") }))}
            />
          </label>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button type="button" className="btn btn--brand" onClick={() => void saveGuidedPractice()}>
              {editingGuidedId ? "Update guided practice" : "Create guided practice"}
            </button>
            {editingGuidedId && (
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setEditingGuidedId(null);
                  setGuidedForm({
                    title: "",
                    description: "",
                    practice_type: "meditation",
                    duration_minutes: 10,
                    script: "",
                    steps: [],
                    is_published: true,
                  });
                }}
              >
                Cancel edit
              </button>
            )}
          </div>

          <div className="admin-table-wrap" style={{ marginTop: "1rem" }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {guidedPractices.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td style={{ textTransform: "capitalize" }}>{item.practice_type}</td>
                    <td>{item.duration_minutes} min</td>
                    <td>{item.is_published ? "Yes" : "No"}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => beginEditGuidedPractice(item)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => void deleteGuidedPractice(item.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Features ────────────────────────────────────────── */}
        <section className="admin-section">
          <h2 className="admin-section__title">
            <span aria-hidden="true">🔧</span> Features
          </h2>
          <div className="admin-toggles">
            <SettingToggle
              label="Gamification"
              hint="XP levels, streaks, badges, and challenge quests on the Practice page."
              value={settings.enableGamification}
              onChange={(v) => updateSettings({ enableGamification: v })}
            />
            <SettingToggle
              label="Newsletter CTA"
              hint="Show the newsletter subscription band in the footer and content pages."
              value={settings.enableNewsletter}
              onChange={(v) => updateSettings({ enableNewsletter: v })}
            />
            <SettingToggle
              label="Maintenance Mode"
              hint="Display a coming-soon message to visitors while you work on updates."
              value={settings.maintenanceMode}
              onChange={(v) => updateSettings({ maintenanceMode: v })}
            />
          </div>
        </section>

        {/* ── Save bar ────────────────────────────────────────── */}
        <div className="admin-save-bar">
          {notice && <span className="admin-save-bar__toast">{notice}</span>}
          {saved && (
            <span className="admin-save-bar__toast">Settings saved successfully</span>
          )}
          <button type="button" className="btn btn--brand" onClick={handleSave}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
